<!--
	Section — pure layout: the grid, max width, gutters and vertical rhythm. It owns
	no typography; `SectionHead` does. Keeping them apart means a section can put a
	set piece full-bleed above its own heading without fighting the wrapper.

	`tone` picks the surface. Paper is the default and most sections keep it; `panel`
	is the recessed well used to bracket a set piece, and `dark` hands the whole
	section to the product's own surface.
-->
<script lang="ts">
	import type { Snippet } from 'svelte';

	type Tone = 'paper' | 'panel' | 'dark';

	let {
		children,
		id,
		as = 'section',
		tone = 'paper',
		/** Drop the max-width/gutters — the child handles its own bleed. */
		bleed = false,
		/** Faint engineering grid behind the content. Set pieces only. */
		grid = false,
		class: klass = ''
	}: {
		children: Snippet;
		id?: string;
		as?: string;
		tone?: Tone;
		bleed?: boolean;
		grid?: boolean;
		class?: string;
	} = $props();
</script>

<svelte:element this={as} {id} class="sec" data-tone={tone} class:sec--dark={tone === 'dark'}>
	{#if grid}
		<div class="sec__grid paper-grid" aria-hidden="true"></div>
	{/if}
	<div class="sec__inner" class:sec__inner--bleed={bleed}>
		<div class={klass}>{@render children()}</div>
	</div>
</svelte:element>

<style>
	.sec {
		position: relative;
		padding-block: var(--spacing-section);
	}
	.sec[data-tone='panel'] {
		background-color: var(--color-panel);
		border-block: 1px solid var(--color-line);
	}
	.sec[data-tone='dark'] {
		background-color: var(--color-app-950);
	}

	.sec__grid {
		position: absolute;
		inset: 0;
		pointer-events: none;
	}

	.sec__inner {
		position: relative;
		margin-inline: auto;
		width: 100%;
		max-width: var(--container-page);
		padding-inline: var(--spacing-gutter);
	}
	.sec__inner--bleed {
		max-width: none;
		padding-inline: 0;
	}
</style>
